import React, { Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import usePrevious from '@dashboard/hooks/usePrevious';
import Button from './Button';

// Radix's autofocus calls select() only for inputs, so a prefilled textarea it
// lands on keeps the caret at 0 and typing prepends. The microtask runs after
// Radix's own focus call, which is synchronous once this handler returns.
const placeCaretAtEndOfFocusedTextarea = event => {
  const content = event.currentTarget;

  queueMicrotask(() => {
    const focused = document.activeElement;

    if (content.contains(focused) && focused.tagName === 'TEXTAREA') {
      focused.setSelectionRange(focused.value.length, focused.value.length);
    }
  });
};

const Dialog = ({
  title,
  children,
  size = 'lg',
  description,
  trigger,
  cancelButton = {
    label: 'Cancel',
    appearance: 'link',
  },
  confirmButton = {
    label: 'Confirm',
    appearance: 'primary',
    disabled: false,
    onClick: () => {},
  },
  onCancel,
  defaultIsOpen = false,
  open = false,
  onOpenChange,
  onRequestClose,
  padded = true,
  className,
  onOpenAutoFocus,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(defaultIsOpen);
  // A custom trigger, the overlay and Escape all change the state inside Radix;
  // without reporting that back, a parent that later sets `open` to false sees
  // no change of its own and the dialog stays up.
  const handleOpenChange = React.useCallback(
    next => {
      // A caller that decides when to close (after its own checks) keeps the dialog open until
      // it sets `open` to false.
      if (!next && onRequestClose) return onRequestClose();
      setDialogOpen(next);
      if (typeof onOpenChange === 'function') onOpenChange(next);
    },
    [onOpenChange, onRequestClose],
  );
  const previousDialogOpen = usePrevious(dialogOpen);

  React.useEffect(() => {
    if (open) {
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!onCancel || typeof onCancel !== 'function') {
      return;
    }

    if (previousDialogOpen && !dialogOpen) {
      onCancel();
    }
  }, [previousDialogOpen, dialogOpen, onCancel]);

  return (
    <DialogPrimitive.Root
      open={dialogOpen}
      onOpenChange={handleOpenChange}
      defaultOpen={defaultIsOpen}
    >
      {trigger && <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>}

      <DialogPrimitive.Portal>
        <Transition show={dialogOpen}>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <Transition
              as="div"
              className={clsx(
                'fixed inset-0 bg-black/50',
                'transition duration-300 ease-in',
                'data-[closed]:opacity-0',
              )}
            >
              <DialogPrimitive.Overlay />
            </Transition>

            <div className="min-h-full flex items-center justify-center">
              <Transition>
                <DialogPrimitive.Content
                  onOpenAutoFocus={event => {
                    onOpenAutoFocus?.(event);
                    if (!event.defaultPrevented) placeCaretAtEndOfFocusedTextarea(event);
                  }}
                  className={clsx(
                    'relative z-50 mx-auto',
                    'w-[95vw] max-h-[90vh] rounded-lg',
                    padded && 'p-6',
                    'bg-white dark:bg-background dark:border dark:border-border overflow-auto',
                    'focus:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                    'transition ease-in-out duration-300',
                    'data-[closed]:opacity-0 data-[closed]:scale-95',
                    'data-[enter]:duration-200',
                    'data-[leave]:duration-150',
                    {
                      'max-w-md': size === 'md',
                      'max-w-lg': size === 'lg',
                      'max-w-xl': size === 'xl',
                      'max-w-2xl': size === '2xl',
                      'max-w-3xl': size === '3xl',
                      'max-w-5xl': size === '5xl',
                    },
                    className,
                  )}
                >
                  {title && (
                    <DialogPrimitive.Title className="text-lg font-bold text-gray-900 dark:text-foreground">
                      {title}
                    </DialogPrimitive.Title>
                  )}

                  {description && (
                    <DialogPrimitive.Description asChild>
                      <div className="mt-2 text-sm text-gray-700 dark:text-muted-foreground">
                        {description}
                      </div>
                    </DialogPrimitive.Description>
                  )}

                  {children != null && (
                    <div className={clsx(padded ? 'mt-4' : 'h-full')}>
                      {typeof children === 'function'
                        ? children({
                            close: () => setDialogOpen(false),
                          })
                        : children}
                    </div>
                  )}

                  {(cancelButton || confirmButton) && (
                    <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 sm:gap-0">
                      {cancelButton && (
                        <DialogPrimitive.Close onClick={onCancel} asChild>
                          <Button {...cancelButton}>{cancelButton.label}</Button>
                        </DialogPrimitive.Close>
                      )}

                      {confirmButton && <Button {...confirmButton}>{confirmButton.label}</Button>}
                    </div>
                  )}

                  <DialogPrimitive.Close asChild>
                    <button
                      type="button"
                      aria-label="Close"
                      className="absolute top-4 right-4 text-foreground/70 hover:text-foreground transition-colors focus:outline-none pointer-coarse:after:absolute pointer-coarse:after:-inset-2.5"
                    >
                      <XMarkIcon width="24px" height="24px" className="fill-current" />
                    </button>
                  </DialogPrimitive.Close>
                </DialogPrimitive.Content>
              </Transition>
            </div>
          </div>
        </Transition>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default Dialog;
